import type {
    SketchCircularDistributionInput,
    SketchCircularDistributionItem,
} from '../../../types/sketch/circular-pattern/SketchCircularDistributionTypes';

/** 完整圆周弧度。 */
const FULL_CIRCLE_RADIANS = Math.PI * 2;

/** 角度比较容差。 */
const ANGLE_EPSILON = 1e-10;

/**
 * 草图圆周分布计算器。
 *
 * 职责：
 * 1. 将圆周阵列参数转换为稳定实例索引及旋转角；
 * 2. 统一完整圆周与部分圆弧的等间距规则；
 * 3. 过滤可跳过实例，但始终保留源实例。
 *
 * 本类只负责数学计算，不创建草图对象、不持有运行时状态，也不提交历史事务。
 */
export class SketchCircularDistributionCalculator {
    /**
     * 计算圆周分布实例。
     *
     * 规则：
     * 1. 实例数包含索引 0 的源实例；
     * 2. 完整 360 度等间距时使用 `总角度 / 实例数`，避免首尾重复；
     * 3. 部分圆弧等间距时使用 `总角度 / (实例数 - 1)`，使首末实例落在范围两端；
     * 4. 非等间距模式直接使用调用方给出的相邻实例角度；
     * 5. 方向只改变角度符号，不改变稳定实例索引。
     *
     * @param input 圆周分布输入。
     * @returns 未被跳过的实例结果，按稳定实例索引升序排列。
     */
    public calculate(input: SketchCircularDistributionInput): readonly SketchCircularDistributionItem[] {
        this.validateInput(input);

        const directionSign = input.direction === 'clockwise' ? -1 : 1;
        const spacingAngleRad = this.resolveSpacingAngle(input);
        const skippedIndexes = this.resolveSkippedIndexes(input);
        const items: SketchCircularDistributionItem[] = [];

        for (let instanceIndex = 0; instanceIndex < input.instanceCount; instanceIndex += 1) {
            // 源实例是阵列定义基准，不能通过“可跳过实例”移除。
            if (instanceIndex !== 0 && skippedIndexes.has(instanceIndex)) {
                continue;
            }

            // 索引 0 必须输出规范化正零；反向模式下的 `-1 * 0` 会产生 `-0`，破坏稳定快照比较。
            const rotationRad = instanceIndex === 0
                ? 0
                : directionSign * spacingAngleRad * instanceIndex;
            items.push({
                instanceIndex,
                angleRad: input.startAngleRad + rotationRad,
                rotationRad,
                isSource: instanceIndex === 0,
            });
        }

        return items;
    }

    /**
     * 解析相邻实例角度。
     *
     * @param input 圆周分布输入。
     * @returns 相邻实例角度，单位为弧度。
     */
    private resolveSpacingAngle(input: SketchCircularDistributionInput): number {
        if (!input.equalSpacing) {
            return input.spacingAngleRad as number;
        }

        const isFullCircle = Math.abs(Math.abs(input.totalAngleRad) - FULL_CIRCLE_RADIANS) <= ANGLE_EPSILON;
        if (isFullCircle) {
            return Math.abs(input.totalAngleRad) / input.instanceCount;
        }

        return input.instanceCount === 1
            ? 0
            : Math.abs(input.totalAngleRad) / (input.instanceCount - 1);
    }

    /**
     * 规范化跳过索引。
     *
     * @param input 圆周分布输入。
     * @returns 去重后的有效跳过索引集合。
     */
    private resolveSkippedIndexes(input: SketchCircularDistributionInput): ReadonlySet<number> {
        const skippedIndexes = new Set<number>();
        input.skippedInstanceIndexes?.forEach((instanceIndex) => {
            // 无效索引直接忽略，避免预览阶段因旧实例选择阻断参数编辑。
            if (Number.isInteger(instanceIndex) && instanceIndex > 0 && instanceIndex < input.instanceCount) {
                skippedIndexes.add(instanceIndex);
            }
        });
        return skippedIndexes;
    }

    /**
     * 校验计算输入。
     *
     * @param input 圆周分布输入。
     * @throws 当实例数、角度或非等间距参数非法时抛出明确错误。
     */
    private validateInput(input: SketchCircularDistributionInput): void {
        if (!Number.isInteger(input.instanceCount) || input.instanceCount < 1) {
            throw new Error('圆周分布实例数必须是大于等于 1 的整数。');
        }
        if (!Number.isFinite(input.startAngleRad)) {
            throw new Error('圆周分布起始角必须是有限数值。');
        }
        if (!Number.isFinite(input.totalAngleRad) || Math.abs(input.totalAngleRad) <= ANGLE_EPSILON) {
            throw new Error('圆周分布总角度必须是非零有限数值。');
        }
        if (!input.equalSpacing && (!Number.isFinite(input.spacingAngleRad) || Math.abs(input.spacingAngleRad as number) <= ANGLE_EPSILON)) {
            throw new Error('非等间距圆周分布必须提供非零有限的相邻实例角度。');
        }
    }
}
